import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../contexts/LocaleContext';
import { Mail, MapPin, Phone } from 'lucide-react';
import { toast } from 'sonner';

const Footer = () => {
  const { t } = useTranslation();
  const { localizedPath } = useLocale();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error(t('contact.error') || 'Please enter your email');
      return;
    }

    if (!consent) {
      toast.error('Please accept the privacy policy');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Invalid email address.');
      return;
    }

    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Successfully subscribed ✅');
        setTimeout(() => {
          navigate(localizedPath('thank-you?source=newsletter'));
        }, 500);
      } else {
        toast.error(data.detail || 'Failed ❌ Write to us: digitalcorpfrance@gmail.com');
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Error subscribing:', error);
      toast.error('Failed ❌ Write to us: digitalcorpfrance@gmail.com');
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#0A0A0A] text-white">
      <div className="container">
        <div className="py-16 border-b border-gray-800">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="text-2xl font-bold mb-3">{t('footer.newsletter')}</h3>
            <p className="text-gray-400 mb-6">
              {t('footer.newsletterDesc')}
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col gap-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('footer.emailPlaceholder')}
                className="px-4 py-3 rounded-lg bg-gray-900 border border-gray-700 text-white placeholder-gray-500 focus:border-[#0B3F84] focus:ring-2 focus:ring-[#0B3F84]/20"
                disabled={isSubmitting}
              />
              <div className="flex items-start gap-2 text-left">
                <input
                  type="checkbox"
                  id="footer-consent"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1"
                  disabled={isSubmitting}
                />
                <label htmlFor="footer-consent" className="text-xs text-gray-400">
                  I accept to receive emails and the{' '}
                  <Link to={localizedPath('privacy')} className="text-[#0B3F84] hover:underline">
                    privacy policy
                  </Link>
                </label>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-3 bg-[#0B3F84] text-white font-semibold rounded-lg hover:bg-[#0a3570] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? t('footer.subscribing') : t('footer.subscribe')}
              </button>
            </form>
          </div>
        </div>

        <div className="py-12 grid md:grid-cols-4 gap-8">
          <div>
            <h4 className="font-bold mb-4">{t('footer.company')}</h4>
            <ul className="space-y-2">
              <li>
                <Link to={localizedPath('')} className="text-gray-400 hover:text-white transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to={localizedPath('services')} className="text-gray-400 hover:text-white transition-colors">
                  {t('nav.services')}
                </Link>
              </li>
              <li>
                <Link to={localizedPath('assessment')} className="text-gray-400 hover:text-white transition-colors">
                  {t('nav.assessment')}
                </Link>
              </li>
              <li>
                <Link to={localizedPath('blog')} className="text-gray-400 hover:text-white transition-colors">
                  {t('nav.blog')}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold mb-4">{t('footer.legal')}</h4>
            <ul className="space-y-2">
              <li>
                <Link to={localizedPath('legal-notice')} className="text-gray-400 hover:text-white transition-colors">
                  {t('footer.legalNotice')}
                </Link>
              </li>
              <li>
                <Link to={localizedPath('privacy-policy')} className="text-gray-400 hover:text-white transition-colors">
                  {t('footer.privacyPolicy')}
                </Link>
              </li>
              <li>
                <Link to={localizedPath('terms-of-service')} className="text-gray-400 hover:text-white transition-colors">
                  {t('footer.termsOfService')}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold mb-4">{t('footer.contact')}</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-gray-400">
                <Mail size={16} />
                <a href="mailto:digitalcorpfrance@gmail.com" className="hover:text-white transition-colors">
                  digitalcorpfrance@gmail.com
                </a>
              </li>
              <li className="flex items-center gap-2 text-gray-400">
                <Phone size={16} />
                <span>+33 6 30 28 95 74</span>
              </li>
              <li className="flex items-start gap-2 text-gray-400">
                <MapPin size={16} className="mt-1" />
                <span>52 rue des Franciscains<br />68100 Mulhouse, France</span>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold mb-4">Business Info</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li>SIRET: 75229266400022</li>
              <li>VAT: Not applicable, art. 293 B CGI</li>
            </ul>
          </div>
        </div>

        <div className="py-6 border-t border-gray-800 text-center text-gray-400 text-sm">
          <p>{t('footer.rights').replace('2024', new Date().getFullYear())}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
